import { usePageSize } from "@/lib/pagesize";
import { useTableSort } from "@/lib/sort";
import { useLocalServerSort } from "@/lib/sort";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { Link, navigate, useSearch, useTitle } from "@/lib/router";
import { COMM_STATUS_LABEL, fmtDate, fmtDateTime, MOVE_KIND_LABEL, MOVE_KIND_TONE, SOURCE_LABEL, todayIso } from "@/lib/format";
import { NewActivityModal } from "./ActivityPages";
import { staleRange, useSavedState } from "@/lib/prefs";
import { FilterNotice } from "@/ui/filter-notice";
import { useLookups } from "@/lib/catalog";
import {
  Badge, Card, DataTable, ErrorBox, Field, FormGrid, Loading, Modal, PageHeader, Pager, SearchBox, Stat, Tabs, useAct, Cnj, CopyButton, ReasonDialog,
} from "@/ui/kit";
import { MenuBoard, ModeSwitch, useBoardPref } from "@/ui/board";
import { DismissButton, type Movement } from "@/ui/movements";
import { MailQueue, useMailSummary } from "./MailPages";

interface Summary {
  movementsWithoutComm?: number;
  pendingAnalysis: number; lateAnalysis: number; unlinked: number; unverifiedObligations: number;
  processesWithoutReview30d: number; fatalNext7Days: number;
  oabs: Array<{ id: string; number: string; uf: string; holderName: string; nameVariants?: string[]; userId?: string | null; lastCheckpoint: string | null }>;
  coverage?: Record<string, Array<{ tribunal: string; count: number; last: string | null }>>;
  sources: Array<{ source: string; status: "verde" | "amarelo" | "vermelho" | "nao_implementada"; lastRunAt: string | null; lastOkAt: string | null; lastError: string | null; lastFetched: number; lastCreated: number }>;
}
interface CommRow {
  id: string; cnjNumber: string | null; processId: string | null; organName: string | null; kind: string | null; text: string;
  availableAt: string | null; status: string; sourcesCount: number; createdAt: string; sources: string[]; late: boolean;
  _count: { obligations: number };
}

type Tab = "caixa" | "todas" | "movimentacoes" | "emails" | "discrepancias" | "fontes";

export function ControlPage(): JSX.Element {
  useTitle("Controladoria");
  const [search, set] = useSearch();
  const tab = (search.get("aba") as Tab) || "caixa";
  const openId = search.get("com");
  // Links antigos (?com=) abrem a tela cheia da intimação.
  useEffect(() => { if (openId) navigate(`/controladoria/intimacoes/${openId}`, { replace: true }); }, [openId]);
  const open = (id: string, fila?: string, filters: Record<string, string> = {}) => navigate(`/controladoria/intimacoes/${id}${qs({ fila: fila ?? "todas", ...filters })}`);
  const { data: s, error } = useQuery({ queryKey: ["controladoria", "resumo"], queryFn: () => api.get<Summary>("/controladoria/resumo"), refetchInterval: 60_000 });
  const failing = s?.sources.filter((x) => x.status === "vermelho") ?? [];
  const { data: mail } = useMailSummary();

  return (
    <div className="stack gap-16">
      <PageHeader title="Controladoria" subtitle="Comunicações capturadas, análise e conferência" actions={<ManualButton />} />
      <ErrorBox error={error} />
      {failing.length > 0 && (
        <div className="banner-bad">
          ⚠ Falha de captura: {failing.map((f) => `${SOURCE_LABEL[f.source]}${f.lastError ? ` (${f.lastError})` : " (sem execução)"}`).join(" · ")}. Confira os tribunais manualmente até normalizar.
        </div>
      )}
      {s ? (
        <div className="grid cols-4">
          <Stat label="Não analisadas" value={s.pendingAnalysis} tone={s.pendingAnalysis ? "warn" : "ok"} onClick={() => set({ aba: "caixa" })} />
          <Stat label="Há mais de 48h" value={s.lateAnalysis} tone={s.lateAnalysis ? "bad" : "ok"} />
          <Stat label="Sem processo vinculado" value={s.unlinked} tone={s.unlinked ? "warn" : undefined} />
          <Stat label="Obrigações não verificadas" value={s.unverifiedObligations} tone={s.unverifiedObligations ? "info" : undefined} />
          {!!s.movementsWithoutComm && <Stat label="Movimentações sem intimação" value={s.movementsWithoutComm} tone="warn" onClick={() => set({ aba: "movimentacoes" })} hint="Avisos do tribunal + movimentações sem intimação (90 dias)" />}
        </div>
      ) : <Loading />}
      <Tabs<Tab> value={tab} onChange={(t) => set({ aba: t })} tabs={[
        { key: "caixa", label: "Caixa de análise", count: s?.pendingAnalysis },
        { key: "todas", label: "Todas" },
        { key: "movimentacoes", label: "Movimentações sem intimação", count: s?.movementsWithoutComm },
        ...(mail && (mail.canPush || mail.canActivity) ? [{ key: "emails" as const, label: "E-mails", count: mail.pending }] : []),
        { key: "discrepancias", label: "Divergências entre fontes" },
        { key: "fontes", label: "Fontes e OABs" },
      ]} />
      {tab === "caixa" && <CommList status="nao_analisada" onOpen={(id, fl) => open(id, "nao_analisada", fl)} />}
      {tab === "todas" && <CommList onOpen={(id, fl) => open(id, undefined, fl)} />}
      {tab === "movimentacoes" && <UnmatchedMovements />}
      {tab === "emails" && <MailQueue />}
      {tab === "discrepancias" && <Discrepancies onOpen={(id) => open(id)} />}
      {tab === "fontes" && s && <Sources s={s} />}
    </div>
  );
}

const COUNTER_PRESETS: Record<string, Partial<CommFilters>> = {
  hoje: { period: "hoje", st: "" }, aberto: { period: "", st: "nao_analisada" }, atrasadas: { period: "", st: "nao_analisada", late: true },
  semVinculo: { period: "", st: "nao_analisada", unlinked: true }, capturadasHoje: { period: "hoje", basis: "captura", st: "" },
};
interface CommFilters extends Record<string, unknown> { q: string; st: string; unlinked: boolean; late: boolean; period: string; from: string; to: string; basis: "disponibilizacao" | "captura"; source: string; oab: string; lawyer: string; responsible: string }
const PERIODS: Record<string, string> = { "": "Qualquer data", hoje: "Hoje", ontem: "Ontem", "7d": "Últimos 7 dias", "30d": "Últimos 30 dias", custom: "Período…" };
function periodRange(p: string, from: string, to: string): { from?: string; to?: string } {
  const t = todayIso();
  const minus = (n: number) => { const d = new Date(`${t}T12:00:00Z`); d.setUTCDate(d.getUTCDate() - n); return d.toISOString().slice(0, 10); };
  if (p === "hoje") return { from: t, to: t };
  if (p === "ontem") return { from: minus(1), to: minus(1) };
  if (p === "7d") return { from: minus(6), to: t };
  if (p === "30d") return { from: minus(29), to: t };
  if (p === "custom") return { from: from || undefined, to: to || undefined };
  return {};
}
function shiftDay(iso: string, n: number): string { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); }

interface LawyerRow { key: string; name: string; userId: string | null; oabs: string[]; names: string[]; open: number; late: number; conferir: number }
export function useControlLawyers() {
  return useQuery({ queryKey: ["controladoria", "advogados"], queryFn: () => api.get<LawyerRow[]>("/controladoria/advogados"), refetchInterval: 60_000 });
}

function CommList({ status, onOpen }: { status?: string; onOpen: (id: string, filters: Record<string, string>) => void }): JSX.Element {
  const defaults: CommFilters = { q: "", st: status ?? "", unlinked: false, late: false, period: "", from: "", to: "", basis: "disponibilizacao", source: "", oab: "", lawyer: "", responsible: "" };
  const [f, upF, resetF, meta] = useSavedState<CommFilters>(`controladoria:${status ? "caixa" : "todas"}`, defaults, { skip: ["q"], dateKeys: ["from", "to"] });
  const [skip, setSkip] = useState(0);
  const up = (x: Partial<CommFilters>) => { upF(x); setSkip(0); };
  const board = useBoardPref("comunicacoes");
  const pageSize = usePageSize();
  const { catalog, userName } = useLookups();
  const users = catalog?.users;
  const { data: lawyers } = useControlLawyers();
  const range = periodRange(f.period, f.from, f.to);
  const params = {
    q: f.q, status: f.st, unlinked: f.unlinked || undefined, late: f.late || undefined, from: range.from, to: range.to,
    dateBasis: range.from || range.to ? f.basis : undefined, source: f.source || undefined, oab: f.oab || undefined,
    lawyer: f.lawyer || undefined, responsible: f.responsible || undefined,
  };
  const { data: cnt } = useQuery({ queryKey: ["controladoria", "contadores"], queryFn: () => api.get<{ hoje: number; aberto: number; atrasadas: number; semVinculo: number; capturadasHoje: number; oabs: string[] }>("/controladoria/contadores"), refetchInterval: 60_000 });
  const srv = useLocalServerSort("comunicacoes");
  const { data, error } = useQuery({
    enabled: board.mode === "lista",
    queryKey: ["comunicacoes", params, skip, pageSize, srv.params],
    queryFn: () => api.get<{ items: CommRow[]; total: number; take: number; skip: number }>(`/comunicacoes${qs({ ...params, ...srv.params, skip, take: pageSize })}`),
  });
  const active = meta.changed.length;
  // Caixa: quantas em aberto (e atrasadas) ficaram FORA do filtro.
  const riskOn = status === "nao_analisada" && active > 0;
  const { data: lateIn } = useQuery({
    enabled: riskOn, queryKey: ["comunicacoes", "atrasadas-no-filtro", params],
    queryFn: () => api.get<{ total: number }>(`/comunicacoes${qs({ ...params, late: true, take: 1 })}`),
  });
  const risk = riskOn && cnt && data ? {
    hidden: Math.max(0, cnt.aberto - data.total), urgent: lateIn ? Math.max(0, cnt.atrasadas - lateIn.total) : 0,
    noun: ["intimação em aberto", "intimações em aberto"] as [string, string], urgentNoun: ["intimação atrasada (+48h)", "intimações atrasadas (+48h)"] as [string, string],
  } : null;
  const lawyerName = (k: string) => lawyers?.find((l) => l.key === k)?.name ?? "advogado";
  const LABEL: Record<string, (v: unknown) => [string, string?]> = {
    st: (v) => ["Situação", COMM_STATUS_LABEL[v as string] ?? String(v)], unlinked: () => ["Só sem processo"], late: () => ["Só atrasadas"],
    period: (v) => ["Período", v === "custom" ? `${f.from ? fmtDate(f.from) : "…"} a ${f.to ? fmtDate(f.to) : "…"}` : PERIODS[v as string]],
    from: () => ["", ""], to: () => ["", ""], basis: (v) => ["Data", v === "captura" ? "captura" : "disponibilização"],
    source: (v) => ["Fonte", SOURCE_LABEL[v as string] ?? String(v)], oab: (v) => ["OAB", String(v)],
    lawyer: (v) => ["Advogado", lawyerName(v as string)], responsible: (v) => ["Responsável", v === "sem" ? "sem processo" : userName(v as string)],
  };
  const items = meta.changed.filter((k) => k !== "from" && k !== "to").map((k) => { const [label, value] = LABEL[k as string]?.(f[k]) ?? [String(k)]; return { label, value }; });
  const stale = f.period === "custom" ? staleRange(f.from, f.to) : null;
  const filtersQs = (): Record<string, string> => Object.fromEntries(Object.entries(params).filter(([k, v]) => k !== "status" && v !== undefined && v !== "").map(([k, v]) => [k, String(v)]));
  const applyCounter = (k: string) => up({ unlinked: false, late: false, basis: "disponibilizacao", ...COUNTER_PRESETS[k] });
  // ◀ ▶ num período de um dia anda de dia em dia.
  const step = (n: number) => {
    const r = periodRange(f.period, f.from, f.to);
    const base = r.from ?? todayIso();
    const d = shiftDay(base, n);
    up({ period: "custom", from: d, to: f.period === "7d" || f.period === "30d" ? shiftDay(r.to ?? todayIso(), n) : d });
  };
  const withWork = (lawyers ?? []).filter((l) => (status ? l.open : l.open + l.conferir) > 0);
  return (
    <Card>
      {cnt && (
        <div className="counter-row">
          {([["hoje", "Disponibilizadas hoje", cnt.hoje, "info"], ["capturadasHoje", "Capturadas hoje", cnt.capturadasHoje, "info"], ["aberto", "Em aberto", cnt.aberto, cnt.aberto ? "warn" : "ok"], ["atrasadas", "Atrasadas (+48h)", cnt.atrasadas, cnt.atrasadas ? "bad" : "ok"], ["semVinculo", "Sem processo", cnt.semVinculo, cnt.semVinculo ? "warn" : "ok"]] as const)
            .map(([k, l, v, tone]) => <button key={k} type="button" className={`counter ${tone}`} onClick={() => applyCounter(k)}><strong>{v}</strong><span>{l}</span></button>)}
        </div>
      )}
      {status === "nao_analisada" && withWork.length > 0 && (
        <div className="lawyer-chips">
          <span className="muted small">Em aberto por advogado:</span>
          {withWork.map((l) => (
            <button key={l.key} type="button" className={`chip-btn ${f.lawyer === l.key ? "on" : ""}`} title={`OAB ${l.oabs.join(", ") || "—"}`}
              onClick={() => up({ lawyer: f.lawyer === l.key ? "" : l.key })}>
              {l.name} <b>{l.open}</b>{l.late > 0 && <span className="text-bad"> · {l.late} atrasada{l.late > 1 ? "s" : ""}</span>}
            </button>
          ))}
        </div>
      )}
      <FilterNotice items={items} restored={meta.restored} stale={stale} risk={risk} onClear={() => { resetF(); setSkip(0); }} />
      <div className="filter-bar">
        <SearchBox value={f.q} onChange={(v) => up({ q: v })} placeholder="Texto ou número do processo…" />
        <div className="row nowrap">
          <button className="btn small" title="Anterior" onClick={() => step(-1)}>◀</button>
          <select className="input" value={f.period} onChange={(e) => up({ period: e.target.value, ...(e.target.value === "custom" && !f.from ? { from: todayIso(), to: todayIso() } : {}) })}>
            {Object.entries(PERIODS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {f.period === "custom" && <><input type="date" className="input" value={f.from} onChange={(e) => up({ from: e.target.value })} /><span className="muted small">até</span><input type="date" className="input" value={f.to} onChange={(e) => up({ to: e.target.value })} /></>}
          <button className="btn small" title="Próximo" onClick={() => step(1)}>▶</button>
        </div>
        {f.period && <select className="input" value={f.basis} onChange={(e) => up({ basis: e.target.value as CommFilters["basis"] })} title="Qual data usar">
          <option value="disponibilizacao">pela disponibilização</option><option value="captura">pela captura no sistema</option>
        </select>}
        {!status && (
          <select className="input" value={f.st} onChange={(e) => up({ st: e.target.value })}>
            <option value="">Todas as situações</option>
            {Object.entries(COMM_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{k === "nao_analisada" ? "Em aberto (não analisadas)" : v}</option>)}
          </select>
        )}
        {!!lawyers?.length && (
          <select className="input" value={f.lawyer} onChange={(e) => up({ lawyer: e.target.value })} title="Advogado intimado (OAB na captura do DJEN ou acesso ao tribunal)">
            <option value="">Todos os advogados</option>{lawyers.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
          </select>
        )}
        <select className="input" value={f.responsible} onChange={(e) => up({ responsible: e.target.value })} title="Responsável pelo processo">
          <option value="">Todos os responsáveis</option><option value="sem">Sem processo vinculado</option>
          {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
        </select>
        <select className="input" value={f.source} onChange={(e) => up({ source: e.target.value })}>
          <option value="">Todas as fontes</option>{["djen", "datajud", "tribunal", "manual", "advbox"].map((x) => <option key={x} value={x}>{SOURCE_LABEL[x] ?? x}</option>)}
        </select>
        {!!cnt?.oabs.length && <select className="input" value={f.oab} onChange={(e) => up({ oab: e.target.value })}><option value="">Todas as OABs</option>{cnt.oabs.map((o) => <option key={o}>{o}</option>)}</select>}
        <label className="check"><input type="checkbox" checked={f.unlinked} onChange={(e) => up({ unlinked: e.target.checked })} /> Só sem processo</label>
        <label className="check"><input type="checkbox" checked={f.late} onChange={(e) => up({ late: e.target.checked })} /> Só atrasadas</label>
        <span className="spacer" />
        <ModeSwitch mode={board.mode} onChange={board.setMode} />
      </div>
      {board.mode === "quadro" && board.meta && <MenuBoard menu="comunicacoes" meta={board.meta} filters={{ q: f.q, status: status ? undefined : f.st || undefined, unlinked: f.unlinked }} />}
      {board.mode === "lista" && <>
      <ErrorBox error={error} />
      <DataTable rows={data?.items} rowKey={(c) => c.id} onRowClick={(c) => onOpen(c.id, filtersQs())} rowClass={(c) => (c.late ? "row-bad" : "")} server={{ sort: srv.sort, set: (s) => { srv.set(s); setSkip(0); } }}
        empty={active ? "Nada com esses filtros." : status ? "Caixa vazia. Tudo analisado." : "Nenhuma comunicação."}
        columns={[
          { key: "d", label: "Disponib.", width: 96, sortKey: "disponibilizacao", render: (c) => <>{fmtDate(c.availableAt)}<div className="muted small" title="Capturada em">{fmtDateTime(c.createdAt)}</div></> },
          { key: "p", label: "Processo", width: 210, sortKey: "processo", render: (c) => c.cnjNumber ? <Cnj value={c.cnjNumber} /> : <Badge tone="warn">sem número</Badge> },
          { key: "o", label: "Órgão / tipo", sortKey: "orgao", render: (c) => <>{c.organName ?? "—"}<div className="muted small">{c.kind ?? ""}</div></> },
          { key: "t", label: "Trecho", render: (c) => <span className="small">{c.text.slice(0, 140)}</span> },
          { key: "f", label: "Fontes", width: 110, render: (c) => <div className="chips">{c.sources.map((x) => <Badge key={x} tone="info">{SOURCE_LABEL[x] ?? x}</Badge>)}</div> },
          { key: "s", label: "Situação", width: 120, sortKey: "situacao", render: (c) => <Badge tone={c.status === "nao_analisada" ? (c.late ? "bad" : "warn") : "neutral"}>{COMM_STATUS_LABEL[c.status]}</Badge> },
        ]} />
      {data && <Pager total={data.total} take={data.take} skip={data.skip} onChange={setSkip} />}
      </>}
    </Card>
  );
}

function Discrepancies({ onOpen }: { onOpen: (id: string) => void }): JSX.Element {
  const { data, error } = useQuery({
    queryKey: ["controladoria", "discrepancias"],
    queryFn: () => api.get<Array<{ cnjNumber: string; availableAt: string; organName: string | null; communicationIds: string[] }>>("/controladoria/discrepancias"),
  });
  return (
    <Card title="Mesmo processo, dia e órgão com textos diferentes" actions={<span className="muted small">Confira e agrupe se forem a mesma comunicação</span>}>
      <ErrorBox error={error} />
      <DataTable rows={data} rowKey={(r) => r.communicationIds.join()} empty="Nenhuma divergência."
        columns={[
          { key: "p", label: "Processo", render: (r) => <Cnj value={r.cnjNumber} /> },
          { key: "d", label: "Data", render: (r) => fmtDate(r.availableAt) },
          { key: "o", label: "Órgão", render: (r) => r.organName ?? "—" },
          { key: "c", label: "Comunicações", render: (r) => <div className="chips">{r.communicationIds.map((id, i) => <button key={id} className="btn small" onClick={() => onOpen(id)}>versão {i + 1}</button>)}</div> },
        ]} />
    </Card>
  );
}

function Sources({ s }: { s: Summary }): JSX.Element {
  const { can } = useLookups();
  const canRun = can("comunicacao.triar");
  const [djen, setDjen] = useState(false);
  const [trib, setTrib] = useState(false);
  const qc = useQueryClient();
  const run = useAct((x: { source: string; from?: string; to?: string; extra?: TribRun }) => api.post<{ queued: boolean }>(`/controladoria/capturas/${x.source}/executar`, { from: x.from ?? null, to: x.to ?? null, ...(x.extra ?? {}) }),
    [["controladoria-capturas"]], () => { setTimeout(() => void qc.invalidateQueries({ queryKey: ["controladoria"] }), 15_000); }, "Execução iniciada — o resultado aparece em instantes no histórico abaixo");
  return (
    <div className="stack gap-16">
      <ErrorBox error={run.error} />
      <Card title="Vias de captura">
        <div className="source-grid">
          {s.sources.map((x) => (
            <div key={x.source} className="source">
              <div><span className={`dot ${x.status}`} /><strong>{SOURCE_LABEL[x.source]}</strong></div>
              <div className="muted small" style={{ marginTop: 6 }}>
                {x.status === "nao_implementada" ? (x.source === "tribunal" ? "Conector de tribunais (MNI) desligado no servidor" : "Ainda não implementada") : <>
                  Última execução: {fmtDateTime(x.lastRunAt)}<br />Última ok: {fmtDateTime(x.lastOkAt)}<br />
                  {x.source === "datajud" ? <>Última rodada: {x.lastFetched} processos encontrados, {x.lastCreated} movimentações novas</> : <>Última leitura: {x.lastFetched} itens, {x.lastCreated} novos</>}
                </>}
                {x.lastError && <div className="text-bad">{x.lastError}</div>}
              </div>
              {canRun && <div style={{ marginTop: 8 }}>
                {x.source === "djen" && <button className="btn small primary" disabled={run.isPending} onClick={() => setDjen(true)}>Executar agora</button>}
                {x.source === "datajud" && <button className="btn small primary" disabled={run.isPending} onClick={() => run.mutate({ source: "datajud" })}>Executar agora</button>}
                {x.source === "tribunal" && <button className="btn small primary" disabled={run.isPending || x.status === "nao_implementada"} title={x.status === "nao_implementada" ? "Ligue o conector no servidor e em Configurações" : "Consulta avisos e movimentações nos tribunais escolhidos"} onClick={() => setTrib(true)}>Executar agora</button>}
              </div>}
            </div>
          ))}
        </div>
      </Card>
      <CaptureRuns />
      <Card title="OABs monitoradas" actions={can("config.gerenciar") && <Link className="btn small" to="/configuracoes?aba=oabs">Gerenciar</Link>}>
        <div className="muted small pad">
          <strong>Abrangência nacional.</strong> A UF é a da inscrição na OAB, não a do tribunal: a consulta ao DJEN traz publicações
          de todos os tribunais que publicam no DJEN, em qualquer estado. Inscrições suplementares (ex.: PE e BA) entram como OABs
          separadas da mesma pessoa. Tribunais que ainda não publicam no DJEN dependem das outras vias.
        </div>
        <DataTable rows={s.oabs} rowKey={(o) => o.id} empty="Nenhuma OAB cadastrada — sem OAB não há captura."
          columns={[
            { key: "n", label: "OAB", width: 110, render: (o) => <>{o.number}/{o.uf}<div className="muted small">Brasil inteiro</div></> },
            { key: "h", label: "Nomes consultados", render: (o) => <>{o.holderName}{(o.nameVariants ?? []).map((v) => <div key={v} className="small muted">{v}</div>)}</> },
            { key: "t", label: "Tribunais com publicações capturadas", render: (o) => {
              const list = s.coverage?.[`${o.number}/${o.uf}`] ?? [];
              return list.length
                ? <div className="chips">{list.map((c) => <Badge key={c.tribunal} tone="info">{c.tribunal} · {c.count}</Badge>)}</div>
                : <span className="muted small">nenhuma publicação ainda</span>;
            } },
            { key: "c", label: "Capturado até", width: 110, render: (o) => fmtDate(o.lastCheckpoint) },
          ]} />
      </Card>
      {trib && <TribunalRunModal onClose={() => setTrib(false)} onRun={(extra) => { run.mutate({ source: "tribunal", extra }); setTrib(false); }} />}
      {djen && <DjenRunModal onClose={() => setDjen(false)} onRun={(from, to) => { run.mutate({ source: "djen", from, to }); setDjen(false); }} />}
    </div>
  );
}

function DjenRunModal({ onClose, onRun }: { onClose: () => void; onRun: (from?: string, to?: string) => void }): JSX.Element {
  const [mode, setMode] = useState<"novas" | "periodo">("novas");
  const [from, setFrom] = useState(shiftDay(todayIso(), -7));
  const [to, setTo] = useState(todayIso());
  return (
    <Modal title="Executar captura do DJEN agora" onClose={onClose}
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" onClick={() => (mode === "novas" ? onRun() : onRun(from, to))}>Executar</button></>}>
      <div className="stack">
        <label className="check"><input type="radio" checked={mode === "novas"} onChange={() => setMode("novas")} /> <span><strong>Buscar o que é novo</strong> — desde a última captura, em todos os tribunais e diários, para todas as OABs.</span></label>
        <label className="check"><input type="radio" checked={mode === "periodo"} onChange={() => setMode("periodo")} /> <span><strong>Reprocessar um período</strong> — para recuperar algo que possa ter escapado (até 31 dias).</span></label>
        {mode === "periodo" && <FormGrid><Field label="De"><input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} /></Field><Field label="Até"><input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} /></Field></FormGrid>}
        <div className="muted small">O que já foi capturado não duplica. Intervalo mínimo de 5 minutos entre execuções manuais, para não levar bloqueio da fonte.</div>
      </div>
    </Modal>
  );
}

interface TribRun { keys?: string[]; lawyerId?: string | null; since?: string | null; cnj?: string | null }

function TribunalRunModal({ onClose, onRun }: { onClose: () => void; onRun: (x: TribRun) => void }): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["captura-tribunal-opcoes"], queryFn: () => api.get<{ serverEnabled: boolean; tribunais: Array<{ key: string; label: string; url: string | null; probeStatus: string | null }>; advogados: Array<{ id: string; name: string }> }>("/controladoria/capturas/tribunal/opcoes") });
  const [keys, setKeys] = useState<string[]>([]);
  const [lawyerId, setLawyerId] = useState("");
  const [useSince, setUseSince] = useState(false);
  const [since, setSince] = useState(shiftDay(todayIso(), -7));
  const [cnj, setCnj] = useState("");
  const cnjDigits = cnj.replace(/\D/g, "");
  const ok = !cnj || cnjDigits.length === 20;
  return (
    <Modal title="Executar o conector de tribunais agora" onClose={onClose} wide
      footer={<><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={!data?.tribunais.length || !ok} onClick={() => onRun({ keys: keys.length ? keys : undefined, lawyerId: lawyerId || null, since: useSince ? since : null, cnj: cnjDigits || null })}>Executar</button></>}>
      <div className="stack gap-12 small">
        <ErrorBox error={error} />
        {!data ? <Loading /> : !data.tribunais.length ? <div className="warn-box">Nenhum tribunal escolhido. Marque em Configurações › Integrações › Advogados e tribunais.</div> : <>
          <Field label="Tribunais" hint="Nenhum marcado = todos os escolhidos.">
            <div className="row" style={{ flexWrap: "wrap" }}>
              {data.tribunais.map((t) => (
                <label key={t.key} className="row nowrap" title={t.url ?? "endereço ainda não verificado"}>
                  <input type="checkbox" checked={keys.includes(t.key)} onChange={(e) => setKeys(e.target.checked ? [...keys, t.key] : keys.filter((k) => k !== t.key))} /> {t.label}{t.probeStatus && t.probeStatus !== "aberto" ? " ⚠" : ""}
                </label>
              ))}
            </div>
          </Field>
          <FormGrid>
            <Field label="Advogado"><select className="input" value={lawyerId} onChange={(e) => setLawyerId(e.target.value)}><option value="">Todos</option>{data.advogados.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></Field>
            <Field label="Processo específico (CNJ)" hint="Opcional: confere só este processo."><input className="input" placeholder="0000000-00.0000.0.00.0000" value={cnj} onChange={(e) => setCnj(e.target.value)} /></Field>
          </FormGrid>
          <label className="check"><input type="checkbox" checked={useSince} onChange={(e) => setUseSince(e.target.checked)} /> <span><strong>Só movimentações a partir de</strong> — mais rápido; sem isso, traz todas (as já gravadas não duplicam).</span></label>
          {useSince && <Field label="A partir de"><input type="date" className="input" value={since} onChange={(e) => setSince(e.target.value)} /></Field>}
          {!ok && <div className="warn-box">O número CNJ precisa ter 20 dígitos.</div>}
          <div className="muted">Consulta os <b>avisos pendentes</b> (sem abrir o teor) e as <b>movimentações</b> dos processos cadastrados. Intervalo mínimo de 5 minutos entre execuções manuais.</div>
        </>}
      </div>
    </Modal>
  );
}

function CaptureRuns(): JSX.Element {
  const [source, setSource] = useState("");
  const [skip, setSkip] = useState(0);
  const pageSize = usePageSize();
  const csrv = useLocalServerSort("capturas");
  const { data, error } = useQuery({
    queryKey: ["controladoria-capturas", source, skip, pageSize, csrv.params],
    queryFn: () => api.get<{ items: Array<{ id: string; source: string; oab: string | null; startedAt: string; finishedAt: string | null; status: string; fetched: number; created: number; corroborated: number; error: string | null; triggeredByName: string | null; windowFrom: string | null; windowTo: string | null }>; total: number; take: number; skip: number }>(`/controladoria/capturas${qs({ source, skip, take: pageSize, ...csrv.params })}`),
    refetchInterval: (q) => (q.state.data?.items.some((r) => r.status === "running") ? 5_000 : 60_000),
  });
  return (
    <Card title="Histórico de execuções" actions={<select className="input" value={source} onChange={(e) => { setSource(e.target.value); setSkip(0); }}><option value="">Todas as fontes</option><option value="djen">DJEN</option><option value="datajud">DataJud</option><option value="tribunal">Tribunais</option></select>}>
      <ErrorBox error={error} />
      <DataTable rows={data?.items} rowKey={(r) => r.id} empty="Nenhuma execução ainda." server={{ sort: csrv.sort, set: (s) => { csrv.set(s); setSkip(0); } }}
        columns={[
          { key: "q", label: "Início", width: 140, sortKey: "inicio", render: (r) => fmtDateTime(r.startedAt) },
          { key: "s", label: "Fonte", width: 130, sortKey: "fonte", render: (r) => <>{SOURCE_LABEL[r.source] ?? r.source}{r.oab && <div className="muted small">OAB {r.oab}</div>}</> },
          { key: "w", label: "Disparo", width: 160, render: (r) => r.triggeredByName ? <>manual · {r.triggeredByName}</> : <span className="muted">automático</span> },
          { key: "j", label: "Janela", width: 150, render: (r) => r.windowFrom ? `${fmtDate(r.windowFrom)} a ${fmtDate(r.windowTo)}` : "—" },
          { key: "r", label: "Resultado", sortKey: "resultado", render: (r) => r.status === "running" ? <Badge tone="info">executando…</Badge>
            : <>{r.status === "ok" ? <Badge tone="ok">ok</Badge> : <Badge tone="bad">erro</Badge>} <span className="small">{r.fetched} encontrada(s), {r.created} nova(s){r.corroborated ? `, ${r.corroborated} confirmada(s)` : ""}</span>{r.error && <div className={`small ${r.status === "ok" ? "muted" : "text-bad"}`}>{r.error}</div>}</> },
        ]} />
      {data && <Pager total={data.total} take={data.take} skip={data.skip} onChange={setSkip} />}
    </Card>
  );
}


type ProcRef = { id: string; cnjNumber: string | null; title: string | null; organName: string; responsibleUserId: string } | null;
type UMov = Movement & { moveKind: string; process: ProcRef };
interface Aviso {
  id: string; cnjNumber: string | null; processId: string | null; organName: string | null; kind: string | null; availableAt: string | null; createdAt: string;
  process: ProcRef; moveKind: string; tribunal: string | null; advogado: string | null; cienciaTacitaEm: string | null;
}
const UM_KEY = ["controladoria", "movimentacoes"];

function ProcCell({ p, cnj }: { p: ProcRef; cnj?: string | null }): JSX.Element {
  if (!p) return cnj ? <><span className="mono">{cnj}</span><CopyButton text={cnj} title="Copiar número do processo" /><div className="muted small">processo não cadastrado</div></> : <>—</>;
  return <><Link to={`/processos/${p.id}`}><span className="mono">{p.cnjNumber ?? p.title}</span></Link>{p.cnjNumber && <CopyButton text={p.cnjNumber} title="Copiar número do processo" />}<div className="muted small">{p.organName}</div></>;
}

/**
 * Conferência do controlador: tudo o que NÃO é intimação oficial (DJEN) e pode gerar providência.
 * Avisos do painel do tribunal + movimentações (DataJud, tribunal, push por e-mail) sem intimação correspondente.
 */
function UnmatchedMovements(): JSX.Element {
  const { userName, can, catalog } = useLookups();
  const users = catalog?.users;
  const qc = useQueryClient();
  const { data, error } = useQuery({ queryKey: UM_KEY, queryFn: () => api.get<{ movements: UMov[]; avisos: Aviso[] }>("/controladoria/movimentacoes-sem-intimacao") });
  const { data: lawyers } = useControlLawyers();
  const [mf, upMf, resetMf, meta] = useSavedState("controladoria:movimentacoes", { kind: "", src: "", lawyer: "", responsible: "", q: "" }, { skip: ["q"] });
  const { kind, src, q } = mf;
  const setKind = (v: string) => upMf({ kind: v }); const setSrc = (v: string) => upMf({ src: v }); const setQ = (v: string) => upMf({ q: v });
  const [avisoOk, setAvisoOk] = useState<Aviso | null>(null);
  const [act, setAct] = useState<UMov | null>(null);
  const [link, setLink] = useState<UMov | null>(null);
  const fold = (s: string) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const lw = lawyers?.find((l) => l.key === mf.lawyer);
  const respOk = (p: ProcRef) => !mf.responsible || (mf.responsible === "sem" ? !p : p?.responsibleUserId === mf.responsible);
  const movOk = (m: UMov) => (!kind || m.moveKind === kind) && (!src || (m.source ?? "datajud") === src) && respOk(m.process)
    && (!q || fold(`${m.name} ${m.complement ?? ""} ${m.process?.cnjNumber ?? ""} ${m.process?.title ?? ""}`).includes(fold(q)));
  const avisoOk2 = (a: Aviso) => (!kind || a.moveKind === kind) && (!src || src === "tribunal") && respOk(a.process)
    && (!mf.lawyer || (!!lw && !!a.advogado && lw.names.some((n) => fold(n) === fold(a.advogado!))))
    && (!q || fold(`${a.kind ?? ""} ${a.organName ?? ""} ${a.cnjNumber ?? ""} ${a.tribunal ?? ""}`).includes(fold(q)));
  // O advogado intimado só existe nos avisos do tribunal; nas movimentações, o filtro é o responsável.
  const movs = (data?.movements ?? []).filter((m) => movOk(m) && !mf.lawyer);
  const avisos = (data?.avisos ?? []).filter(avisoOk2);
  const ts = useTableSort("movimentacoes-sem-intimacao", movs, {
    data: (m) => new Date(m.occurredAt), processo: (m) => m.process?.cnjNumber ?? m.process?.title ?? null, mov: (m) => m.name,
    tipo: (m) => MOVE_KIND_LABEL[m.moveKind] ?? m.moveKind, fonte: (m) => m.source ?? "datajud", resp: (m) => (m.process ? userName(m.process.responsibleUserId) : null),
  });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const triar = can("comunicacao.triar");
  const hiddenAv = data.avisos.filter((a) => !avisos.includes(a));
  const soonHidden = hiddenAv.filter((a) => a.cienciaTacitaEm && new Date(a.cienciaTacitaEm).getTime() - Date.now() < 3 * 86_400_000).length;
  const hiddenTotal = hiddenAv.length + data.movements.length - movs.length;
  const MLABEL: Record<string, (v: string) => [string, string]> = {
    kind: (v) => ["Tipo", MOVE_KIND_LABEL[v] ?? v], src: (v) => ["Origem", SOURCE_LABEL[v] ?? v],
    lawyer: (v) => ["Advogado", lawyers?.find((l) => l.key === v)?.name ?? "advogado"], responsible: (v) => ["Responsável", v === "sem" ? "sem processo" : userName(v)],
  };
  const notice = <FilterNotice items={meta.changed.map((k) => { const [label, value] = MLABEL[k as string]!(String(mf[k])); return { label, value }; })} restored={meta.restored} onClear={resetMf}
    risk={meta.changed.length ? { hidden: hiddenTotal, urgent: soonHidden, noun: ["item para conferir", "itens para conferir"], urgentNoun: ["aviso com ciência tácita em até 3 dias", "avisos com ciência tácita em até 3 dias"] } : null} />;
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["controladoria"] }), qc.invalidateQueries({ queryKey: ["comunicacoes"] })]);
  const kinds = [...new Set([...data.movements.map((m) => m.moveKind), ...data.avisos.map((a) => a.moveKind)])];
  const srcs = [...new Set([...data.movements.map((m) => m.source ?? "datajud"), ...(data.avisos.length ? ["tribunal"] : [])])];
  return (
    <div className="stack gap-16">
      {notice}
      <div className="row wrap gap-8">
        {!!lawyers?.length && <select className="input" style={{ width: 220 }} value={mf.lawyer} onChange={(e) => upMf({ lawyer: e.target.value })} title="Advogado do acesso ao tribunal (só avisos)">
          <option value="">Todos os advogados</option>{lawyers.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
        </select>}
        <select className="input" style={{ width: 220 }} value={mf.responsible} onChange={(e) => upMf({ responsible: e.target.value })}>
          <option value="">Todos os responsáveis</option><option value="sem">Sem processo vinculado</option>
          {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
        </select>
        <select className="input" style={{ width: 220 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Todos os tipos</option>{kinds.map((k) => <option key={k} value={k}>{MOVE_KIND_LABEL[k] ?? k}</option>)}
        </select>
        <select className="input" style={{ width: 220 }} value={src} onChange={(e) => setSrc(e.target.value)}>
          <option value="">Todas as origens</option>{srcs.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] ?? s}</option>)}
        </select>
        <input className="input" style={{ width: 260 }} placeholder="Processo, movimento, órgão…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card title={`Avisos do painel do tribunal (${avisos.length})`}>
        <div className="muted small" style={{ marginBottom: 8 }}>
          Expedientes pendentes no painel do PJe, capturados pelo conector do tribunal. Não são a intimação oficial (essa chega pelo DJEN e vai para a Caixa de análise);
          o teor não foi aberto, para não dar ciência. Atenção à data da ciência tácita.
        </div>
        {!avisos.length ? <div className="muted">Nenhum aviso para conferir.</div> : (
          <div className="table-wrap"><table className="table">
            <thead><tr><th>Disponível</th><th>Ciência tácita</th><th>Processo</th><th>Tribunal</th><th>Expediente</th><th>Tipo</th><th>Responsável</th><th /></tr></thead>
            <tbody>{avisos.map((a) => {
              const tac = a.cienciaTacitaEm ? new Date(a.cienciaTacitaEm) : null;
              const soon = tac && tac.getTime() - Date.now() < 3 * 86_400_000;
              return (
                <tr key={a.id}>
                  <td>{a.availableAt ? fmtDate(a.availableAt) : "—"}</td>
                  <td>{tac ? <Badge tone={soon ? "bad" : "warn"}>{fmtDate(a.cienciaTacitaEm!)}</Badge> : "—"}</td>
                  <td><ProcCell p={a.process} cnj={a.cnjNumber} /></td>
                  <td>{a.tribunal ?? "—"}{a.advogado && <div className="muted small">{a.advogado}</div>}</td>
                  <td><b>{a.kind ?? "Expediente"}</b>{a.organName && <div className="muted small">{a.organName}</div>}</td>
                  <td><Badge tone={MOVE_KIND_TONE[a.moveKind] ?? "neutral"}>{MOVE_KIND_LABEL[a.moveKind] ?? a.moveKind}</Badge></td>
                  <td>{a.process ? userName(a.process.responsibleUserId) : "—"}</td>
                  <td className="nowrap">
                    <Link className="btn small" to={`/controladoria/intimacoes/${a.id}?fila=conferir`}>Analisar</Link>
                    {triar && <button className="btn small ghost" onClick={() => setAvisoOk(a)}>Conferido</button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </Card>

      <Card title={`Movimentações que podem gerar prazo, sem intimação (${movs.length})`}>
        <div className="muted small" style={{ marginBottom: 8 }}>
          O tribunal registrou estes atos (DataJud, conector do tribunal ou push por e-mail) e nenhuma intimação oficial do mesmo processo foi capturada
          entre 3 dias antes e 15 dias depois. Confira: se houver providência, crie a atividade; se a intimação já existe, vincule; se não, marque como conferida.
        </div>
        {mf.lawyer ? <div className="muted">Com o filtro de advogado, só aparecem os avisos do tribunal (movimentações não têm advogado intimado). Para movimentações, use "Responsável".</div>
          : !movs.length ? <div className="muted">Nada pendente.</div> : (
          <div className="table-wrap"><table className="table">
            <thead><tr>{ts.th("Data", "data")}{ts.th("Processo", "processo")}{ts.th("Movimentação", "mov")}{ts.th("Tipo", "tipo")}{ts.th("Origem", "fonte")}{ts.th("Responsável", "resp")}<th /></tr></thead>
            <tbody>{ts.rows.map((m) => (
              <tr key={m.id}>
                <td>{fmtDate(m.occurredAt)}</td>
                <td><ProcCell p={m.process} /></td>
                <td><b>{m.name}</b>{m.complement && <div className="muted small">{m.complement}</div>}</td>
                <td><Badge tone={MOVE_KIND_TONE[m.moveKind] ?? "neutral"}>{MOVE_KIND_LABEL[m.moveKind] ?? m.moveKind}</Badge></td>
                <td className="small">{SOURCE_LABEL[m.source ?? "datajud"] ?? m.source}</td>
                <td>{m.process ? userName(m.process.responsibleUserId) : "—"}</td>
                <td className="nowrap">
                  {triar && m.process && <button className="btn small" onClick={() => setAct(m)}>Criar atividade</button>}
                  {triar && <button className="btn small ghost" onClick={() => setLink(m)}>Vincular</button>}
                  <DismissButton id={m.id} invalidate={[UM_KEY]} />
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>

      {avisoOk && (
        <ReasonDialog title="Aviso conferido, sem providência" label="O que foi verificado?" confirm="Marcar como conferido" onClose={() => setAvisoOk(null)}
          suggestions={["Ciência registrada no painel; sem prazo", "Já tratado por outra intimação", "Ato meramente ordinatório"]}
          onSubmit={async (reason) => { await api.post(`/comunicacoes/${avisoOk.id}/conferida`, { reason }); await refresh(); }} />
      )}
      {act?.process && (
        <NewActivityModal onClose={() => setAct(null)}
          defaults={{ ctx: { kind: "processo", id: act.process.id, label: act.process.cnjNumber ?? act.process.title ?? "Processo" }, title: `${MOVE_KIND_LABEL[act.moveKind] ?? "Movimentação"}: ${act.name}`.slice(0, 255),
            description: `Movimentação de ${fmtDate(act.occurredAt)} (${SOURCE_LABEL[act.source ?? "datajud"] ?? act.source}): ${act.name}${act.complement ? ` — ${act.complement}` : ""}` }}
          onCreated={async () => {
            await api.post(`/movimentacoes/${act.id}/conferida`, { reason: "Atividade criada a partir da movimentação" }).catch(() => undefined);
            setAct(null); await refresh();
          }} />
      )}
      {link && <LinkToComm m={link} onClose={() => setLink(null)} onDone={async () => { setLink(null); await refresh(); }} />}
    </div>
  );
}

function LinkToComm({ m, onClose, onDone }: { m: UMov; onClose: () => void; onDone: () => Promise<unknown> }): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["mov-intimacoes", m.id], queryFn: () => api.get<Array<{ id: string; kind: string | null; organName: string | null; availableAt: string | null; status: string; text: string }>>(`/movimentacoes/${m.id}/intimacoes`) });
  const go = useAct((communicationId: string) => api.post(`/movimentacoes/${m.id}/vincular`, { communicationId }), [UM_KEY], () => void onDone());
  return (
    <Modal title="Vincular a uma intimação do processo" onClose={onClose} wide footer={<button className="btn" onClick={onClose}>Fechar</button>}>
      <p className="muted small" style={{ marginTop: 0 }}><b>{m.name}</b> · {fmtDate(m.occurredAt)}. Escolha a intimação que corresponde a esta movimentação.</p>
      {error ? <ErrorBox error={error} /> : !data ? <Loading /> : !data.length ? <div className="muted">Nenhuma intimação capturada para este processo.</div> : (
        <div className="stack gap-8">{data.map((c) => (
          <div key={c.id} className="row nowrap gap-8" style={{ alignItems: "flex-start", borderBottom: "1px solid var(--border)", paddingBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <b>{c.availableAt ? fmtDate(c.availableAt) : "—"}</b> · {c.kind ?? "Comunicação"} <Badge tone="neutral">{COMM_STATUS_LABEL[c.status] ?? c.status}</Badge>
              <div className="muted small">{c.organName}</div><div className="small">{c.text}{c.text.length >= 300 ? "…" : ""}</div>
            </div>
            <button className="btn small primary" disabled={go.isPending} onClick={() => go.mutate(c.id)}>Vincular</button>
          </div>
        ))}</div>
      )}
      <ErrorBox error={go.error} />
    </Modal>
  );
}

function ManualButton(): JSX.Element | null {
  const { can } = useLookups();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ cnjNumber: "", organName: "", kind: "", availableAt: todayIso(), text: "" });
  const m = useAct(() => api.post<{ id: string }>("/comunicacoes", { ...f, cnjNumber: f.cnjNumber || null, organName: f.organName || null, kind: f.kind || null }),
    [["comunicacoes"], ["controladoria"]], () => { setOpen(false); setF({ cnjNumber: "", organName: "", kind: "", availableAt: todayIso(), text: "" }); });
  if (!can("comunicacao.triar")) return null;
  return (
    <>
      <button className="btn" onClick={() => setOpen(true)}>Registrar manualmente</button>
      {open && (
        <Modal title="Registrar comunicação manual" onClose={() => setOpen(false)} wide footer={<><button className="btn" onClick={() => setOpen(false)}>Cancelar</button><button className="btn primary" disabled={f.text.trim().length < 5 || m.isPending} onClick={() => m.mutate()}>Registrar</button></>}>
          <FormGrid cols={3}>
            <Field label="Número do processo"><input className="input mono" value={f.cnjNumber} onChange={(e) => setF({ ...f, cnjNumber: e.target.value })} /></Field>
            <Field label="Órgão"><input className="input" value={f.organName} onChange={(e) => setF({ ...f, organName: e.target.value })} /></Field>
            <Field label="Disponibilização"><input type="date" className="input" value={f.availableAt} onChange={(e) => setF({ ...f, availableAt: e.target.value })} /></Field>
            <Field label="Tipo"><input className="input" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} placeholder="Intimação, citação…" /></Field>
            <Field label="Texto" required span={3}><textarea className="input" rows={8} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} /></Field>
          </FormGrid>
          <ErrorBox error={m.error} />
        </Modal>
      )}
    </>
  );
}
